'use client';

import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import type { KolInsight, KolInsightSymbol } from '@flipster/apis/v2/market/types';
import { FormattedMessage } from '@flipster/i18n';
import { Link } from '@flipster/i18n/routing';
import ChevronRightIcon from '@flipster/react/icon/generated/ChevronRightIcon';
import { Styles, css } from '@flipster/styled-system/css';
import { hstack, stack } from '@flipster/styled-system/patterns';

import { rawInstrumentsKeyBySymbolAtom } from '../../shared/snapshot/state';
import {
  filterResolvableSymbols,
  formatInsightSymbolTickers,
  kolInsightMaxVisibleSymbols,
  normalizeIndicators,
} from '../helpers';
import { KolInsightIndicatorIcons } from './indicator-icons';

interface KolInsightCardProps {
  insight: KolInsight;
  href: string;
  css?: Styles;
}

export const KolInsightCard = ({ insight, href, css: cssProps }: KolInsightCardProps) => {
  const instruments = useAtomValue(rawInstrumentsKeyBySymbolAtom);
  const indicators = useMemo(() => normalizeIndicators(insight.indicators), [insight.indicators]);
  const symbols = useMemo(
    () => filterResolvableSymbols(insight.symbols ?? [], (symbol) => Boolean(instruments?.[symbol])),
    [insight.symbols, instruments],
  );

  return (
    <Link
      href={href}
      className={css(
        hstack.raw({
          gap: 8,
          alignItems: 'center',
          p: 16,
          rounded: 12,
          bg: 'background.default.level-0',
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor: 'border.default.level-1',
          h: '100%',
          color: 'content.default.level-5',
          _hover: { bg: 'background.default.level-1' },
        }),
        cssProps,
      )}
    >
      <div className={stack({ gap: 8, flex: 1, minW: 0, h: '100%' })}>
        <KolInsightIndicatorIcons indicators={indicators} />
        <p
          className={css({
            textStyle: 'Body/XL/Monospace/Regular',
            lineClamp: 1,
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          })}
        >
          {insight.title}
        </p>
        <KolInsightCardFooter symbols={symbols} mentions={insight.mentions} />
      </div>
      <ChevronRightIcon size={16} className={css({ color: 'content.default.level-3', flexShrink: 0 })} />
    </Link>
  );
};

const KolInsightCardFooter = ({ symbols, mentions }: { symbols: KolInsightSymbol[]; mentions: number }) => {
  const instruments = useAtomValue(rawInstrumentsKeyBySymbolAtom);
  const tickers = formatInsightSymbolTickers(symbols, (symbol) => instruments?.[symbol]?.displayShortName);

  return (
    <div className={hstack({ gap: 4, mt: 'auto' })}>
      <div className={hstack({ gap: 0 })}>
        {symbols.slice(0, kolInsightMaxVisibleSymbols).map((item, index) => {
          const icon = instruments?.[item.symbol]?.icon;
          if (!icon) return null;
          return (
            <img
              key={item.symbol}
              src={icon}
              alt=''
              className={css({
                w: 20,
                h: 20,
                rounded: '50%',
                borderWidth: 1,
                borderStyle: 'solid',
                borderColor: 'background.default.level-0',
                ml: index === 0 ? 0 : -4,
              })}
            />
          );
        })}
      </div>
      <p className={css({ textStyle: 'Body/M/Monospace/Regular', color: 'content.default.level-3' })}>
        {tickers ? `${tickers} · ` : null}
        {mentions} <FormattedMessage id='Home.r260831.mentions' />
      </p>
    </div>
  );
};

interface KolInsightListRowProps {
  insight: KolInsight;
  href: string;
  selected?: boolean;
  css?: Styles;
}

export const KolInsightListRow = ({ insight, href, selected = false, css: cssProps }: KolInsightListRowProps) => {
  const instruments = useAtomValue(rawInstrumentsKeyBySymbolAtom);
  const indicators = useMemo(() => normalizeIndicators(insight.indicators), [insight.indicators]);
  const symbols = useMemo(
    () => filterResolvableSymbols(insight.symbols ?? [], (symbol) => Boolean(instruments?.[symbol])),
    [insight.symbols, instruments],
  );
  const tickers = formatInsightSymbolTickers(symbols, (symbol) => instruments?.[symbol]?.displayShortName);
  const surfaceBg = selected ? 'background.default.level-1' : 'background.default.level-0';

  return (
    <Link
      href={href}
      className={css(
        hstack.raw({
          gap: 8,
          alignItems: 'center',
          p: 16,
          rounded: 12,
          bg: surfaceBg,
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor: 'border.default.level-1',
          color: 'content.default.level-5',
          _hover: { bg: 'background.default.level-1' },
        }),
        cssProps,
      )}
    >
      <div className={stack({ gap: 8, flex: 1, minW: 0 })}>
        <KolInsightIndicatorIcons indicators={indicators} />
        <p
          className={css({
            textStyle: 'Body/XL/Monospace/Regular',
            lineClamp: 1,
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          })}
        >
          {insight.title}
        </p>
        <div className={hstack({ gap: 8 })}>
          <div className={hstack({ gap: 0 })}>
            {symbols.slice(0, kolInsightMaxVisibleSymbols).map((item, index) => {
              const icon = instruments?.[item.symbol]?.icon;
              if (!icon) return null;
              return (
                <img
                  key={item.symbol}
                  src={icon}
                  alt=''
                  className={css({
                    w: 20,
                    h: 20,
                    rounded: '50%',
                    borderWidth: 1,
                    borderStyle: 'solid',
                    borderColor: surfaceBg,
                    ml: index === 0 ? 0 : -4,
                  })}
                />
              );
            })}
          </div>
          <p className={css({ textStyle: 'Body/M/Monospace/Regular', color: 'content.default.level-3' })}>
            {tickers ? `${tickers} · ` : null}
            {insight.mentions} <FormattedMessage id='Home.r260831.mentions' />
          </p>
        </div>
      </div>
      <ChevronRightIcon size={16} className={css({ color: 'content.default.level-3', flexShrink: 0 })} />
    </Link>
  );
};
